<template>
  <div class="asset-shell">
    <section class="asset-panel">
      <header class="asset-hero">
        <div>
          <p class="asset-hero__eyebrow">素材参考中心</p>
          <h1>一个入口管理内容、图片、视频和项目参考</h1>
          <p class="asset-hero__subtitle">系统内容素材、历史项目经验，以及你本地上传或 AI 生成的图片和视频统一放在这里，减少来回切页找素材。</p>
        </div>
        <div class="asset-hero__actions">
          <el-button v-if="linkedProjectId" plain @click="returnToWorkspace">返回工作台</el-button>
          <el-button type="primary" @click="router.push('/submit')">创建项目</el-button>
        </div>
      </header>

      <el-alert
        v-if="linkedProjectId"
        class="asset-banner"
        type="success"
        :closable="false"
        :title="linkedBannerTitle"
        :description="linkedBannerDescription"
      />

      <section class="asset-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="asset-tab"
          :class="{ 'asset-tab--active': activeTab === tab.key }"
          @click="handleTabChange(tab.key)"
        >
          <span>{{ tab.label }}</span>
          <small>{{ tab.description }}</small>
        </button>
      </section>

      <section v-if="activeTab === 'project'" class="asset-section">
        <div class="query-panel query-panel--single">
          <div class="query-panel__main">
            <div class="section-head">
              <div>
                <span class="section-label">历史项目经验</span>
                <h2>搜索方案、剧本、分镜和角色资料</h2>
                <p>适合找剧情案例、角色设定和镜头表达。长段落检索会自动截短到适合检索的长度。</p>
              </div>
            </div>

            <el-input
              v-model="queryForm.query"
              type="textarea"
              :rows="4"
              maxlength="1000"
              show-word-limit
              placeholder="输入你要找的内容，例如：婚礼主桌旁，女主在混乱中触碰桌角的轻奢茶饮特写"
            />

            <div class="query-controls">
              <div class="query-control query-control--wide">
                <span>检索范围</span>
                <el-select v-model="queryForm.kb_type">
                  <el-option label="全部项目参考" value="all" />
                  <el-option label="剧本与方案" value="script" />
                  <el-option label="分镜参考" value="scene" />
                  <el-option label="角色参考" value="character" />
                </el-select>
              </div>

              <div class="query-control query-control--wide">
                <span>返回数量</span>
                <el-slider v-model="queryForm.top_k" :min="1" :max="12" :step="1" show-input />
              </div>

              <div class="query-actions">
                <el-button type="primary" :loading="querying" @click="handleQuery">开始搜索</el-button>
                <el-button @click="handleClearProjectQuery">清空</el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="quick-tags">
          <button v-for="item in quickQueries" :key="item" type="button" class="quick-tag" @click="quickQuery(item)">
            {{ item }}
          </button>
        </div>

        <section v-if="queryResults.length" class="result-list">
          <article v-for="(result, index) in queryResults" :key="`${result.source}-${index}-${result.created_at}`" class="result-card">
            <div class="result-card__head">
              <div class="pill-row">
                <el-tag size="small" :type="getResultType(result.source)">{{ getResultLabel(result.source) }}</el-tag>
                <el-tag size="small" effect="plain">相似度 {{ (result.score * 100).toFixed(1) }}%</el-tag>
              </div>
              <div class="result-card__actions">
                <el-button
                  v-if="linkedProjectId && canUseResultAsConsistencyAssetImage(result)"
                  size="small"
                  type="primary"
                  plain
                  @click="handleUseResultAsConsistencyAssetImage(result)"
                >
                  设为当前{{ linkedAssetTypeLabel }}参考图
                </el-button>
                <el-button v-else-if="linkedProjectId && canUseResultAsSceneImage(result)" size="small" type="primary" plain @click="handleUseResultAsSceneImage(result)">设为当前分镜图片</el-button>
                <el-button v-else-if="linkedProjectId" size="small" plain @click="linkResultToWorkflow(result)">加入当前项目</el-button>
                <span class="result-card__time">{{ formatDate(result.created_at) }}</span>
              </div>
            </div>

            <div v-if="result.metadata?.image_url" class="result-card__preview">
              <img :src="result.metadata.image_url" :alt="result.metadata?.title || result.metadata?.project_title || '项目参考图'" />
            </div>

            <ReferenceLinkActions v-if="result.metadata?.image_url" :url="result.metadata.image_url" compact />

            <p class="result-card__content">{{ result.content }}</p>

            <div v-if="result.metadata" class="result-card__meta">
              <span v-if="result.metadata.project_title">项目：{{ result.metadata.project_title }}</span>
              <span v-if="result.metadata.title">标题：{{ result.metadata.title }}</span>
              <span v-if="result.metadata.scene_number">场景：第 {{ result.metadata.scene_number }} 场</span>
              <span v-if="result.metadata.location">地点：{{ result.metadata.location }}</span>
              <span v-if="result.metadata.character_name">角色：{{ result.metadata.character_name }}</span>
            </div>
          </article>
        </section>

        <el-empty v-else description="先输入关键词，再从历史项目里找参考" />
      </section>

      <section v-else-if="activeTab === 'content'" class="asset-section">
        <div class="section-head section-head--split">
          <div>
            <span class="section-label">可复用内容</span>
            <h2>系统内容素材</h2>
            <p>这里放已经确认可复用的故事、段子和内容初稿，适合内容驱动项目直接拿来继续创作。</p>
          </div>
          <div class="toolbar-inline">
            <el-input
              v-model="contentFilters.query"
              clearable
              placeholder="搜索内容正文或类型"
              @keyup.enter="loadContentEntries"
              @clear="loadContentEntries"
            />
            <el-select v-model="contentFilters.contentType" clearable placeholder="全部类型">
              <el-option v-for="option in contentTypeOptions" :key="option" :label="option" :value="option" />
            </el-select>
            <el-button type="primary" :loading="loadingContent" @click="loadContentEntries">筛选</el-button>
          </div>
        </div>

        <section v-if="contentEntries.length" class="content-grid">
          <article v-for="entry in contentEntries" :key="entry.id" class="content-card">
            <div class="content-card__head">
              <div class="content-card__identity">
                <div class="pill-row">
                  <el-tag effect="dark" type="primary">{{ entry.content_type }}</el-tag>
                  <el-tag effect="plain">{{ getContentSourceLabel(entry.source) }}</el-tag>
                </div>
                <span class="content-card__time">{{ formatDate(entry.updated_at) }}</span>
              </div>
              <div class="content-card__actions content-card__actions--header">
                <el-button link type="danger" :loading="deletingContentId === entry.id" @click="handleDeleteContentEntry(entry)">删除内容</el-button>
              </div>
            </div>

            <p class="content-card__body">{{ entry.content }}</p>

            <div class="content-card__footer">
              <span>创建于 {{ formatDate(entry.created_at) }}</span>
              <div class="content-card__actions">
                <el-button link type="primary" @click="copyContent(entry.content)">复制内容</el-button>
                <el-button v-if="linkedProjectId" link type="primary" @click="linkContentEntryToWorkflow(entry)">加入当前项目</el-button>
              </div>
            </div>
          </article>
        </section>

        <el-empty v-else description="当前筛选下暂无系统内容素材" />
      </section>

      <MaterialLibrarySection
        v-else-if="activeTab === 'image' || activeTab === 'video' || activeTab === 'audio'"
        :active-tab="activeTab"
        :linked-project-id="linkedProjectId"
        :linked-scene-number="linkedSceneNumber"
        :linked-asset-type-label="linkedAssetTypeLabel"
        :linked-audio-role="linkedAudioRole"
        :is-consistency-asset-image-mode="isConsistencyAssetImageMode"
        :is-final-audio-mode="isFinalAudioMode"
        :image-filters="imageFilters"
        :material-entries="materialEntries"
        :loading-materials="loadingMaterials"
        :deleting-material-id="deletingMaterialId"
        :format-date="formatDate"
        :get-material-source-label="getMaterialSourceLabel"
        :can-delete-material="canDeleteMaterial"
        :load-material-entries="loadMaterialEntries"
        :handle-delete-material="handleDeleteMaterial"
        :handle-use-material-as-final-audio="handleUseMaterialAsFinalAudio"
        :handle-use-material-as-consistency-asset-image="handleUseMaterialAsConsistencyAssetImage"
        :handle-use-material-as-scene-image="handleUseMaterialAsSceneImage"
        :link-material-to-workflow="linkMaterialToWorkflow"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'

import MaterialLibrarySection from './knowledge-base/MaterialLibrarySection.vue'
import ReferenceLinkActions from '@/components/ReferenceLinkActions.vue'
import request from '@/api/request'
import { CONTENT_TYPE_OPTIONS, getContentSourceLabel } from '@/utils/projectModes'
import {
  getConsistencyAssetTypeLabel,
} from './knowledge-base/materialLibrary'
import {
  useKnowledgeBaseLibraries,
} from './knowledge-base/useKnowledgeBaseLibraries'
import {
  useKnowledgeBaseRouteState,
} from './knowledge-base/useKnowledgeBaseRouteState'
import {
  useKnowledgeBaseWorkflowBridge,
} from './knowledge-base/useKnowledgeBaseWorkflowBridge'
import type {
  AssetTab,
  ContentLibraryEntry,
  ContentLibraryFilters,
  KnowledgeQueryForm,
  MaterialLibraryEntry,
  MaterialLibraryFilters,
  QueryResult,
} from './knowledge-base/types'

const route = useRoute()
const router = useRouter()

const tabs: Array<{ key: AssetTab; label: string; description: string }> = [
  { key: 'project', label: '项目参考', description: '方案、剧本、分镜、角色' },
  { key: 'content', label: '内容素材', description: '可复用的内容初稿' },
  { key: 'image', label: '图片素材', description: '本地上传和项目图片' },
  { key: 'video', label: '视频素材', description: 'AI 生成和人工上传视频' },
  { key: 'audio', label: '音频素材', description: '旁白和 BGM 复用素材' },
]

const quickQueries = ['教育手表', '婚礼冲突开场', '林浅角色设定', '身份反转桥段', '亲子成长主题']

const activeTab = ref<AssetTab>('project')
const querying = ref(false)

const queryResults = ref<QueryResult[]>([])

const queryForm = reactive<KnowledgeQueryForm>({
  query: '',
  top_k: 5,
  kb_type: 'all',
})

const contentFilters = reactive<ContentLibraryFilters>({
  query: '',
  contentType: '',
})

const imageFilters = reactive<MaterialLibraryFilters>({
  query: '',
  scope: 'all_projects',
})

const contentTypeOptions = CONTENT_TYPE_OPTIONS

const {
  isConsistencyAssetImageMode,
  isFinalAudioMode,
  isSceneImageMode,
  linkedAssetId,
  linkedAudioRole,
  linkedAssetType,
  linkedAssetTypeLabel,
  linkedBannerDescription,
  linkedBannerTitle,
  linkedProjectId,
  linkedSceneNumber,
  returnToWorkspace,
} = useKnowledgeBaseRouteState({
  route,
  router,
  activeTab,
  queryForm,
  imageFilters,
})

const {
  contentEntries,
  deletingContentId,
  deletingMaterialId,
  handleDeleteContentEntry,
  handleDeleteMaterial,
  loadContentEntries,
  loadMaterialEntries,
  loadingContent,
  loadingMaterials,
  materialEntries,
} = useKnowledgeBaseLibraries({
  activeTab,
  contentFilters,
  imageFilters,
  linkedProjectId,
})

const {
  handleUseMaterialAsConsistencyAssetImage,
  handleUseMaterialAsFinalAudio,
  handleUseMaterialAsSceneImage,
  handleUseResultAsConsistencyAssetImage,
  handleUseResultAsSceneImage,
  linkContentEntryToWorkflow,
  linkMaterialToWorkflow,
  linkResultToWorkflow,
} = useKnowledgeBaseWorkflowBridge({
  activeTab,
  linkedProjectId,
  linkedSceneNumber,
  linkedAssetType,
  linkedAssetId,
  linkedAssetTypeLabel,
  linkedAudioRole,
  returnToWorkspace,
  buildLinkedResultTitle,
  success: (message) => ElMessage.success(message),
})

function formatDate(value?: string | null) {
  if (!value) {
    return '暂无时间'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('zh-CN', { hour12: false })
}

function getResultType(source: string) {
  const typeMap: Record<string, 'success' | 'warning' | 'info'> = {
    script: 'success',
    scene: 'warning',
    character: 'info',
  }
  return typeMap[source] || 'info'
}

function getResultLabel(source: string) {
  const labelMap: Record<string, string> = {
    script: '文本',
    scene: '分镜',
    character: '角色',
  }
  return labelMap[source] || source
}

function getMaterialSourceLabel(source: string) {
  const labels: Record<string, string> = {
    uploaded_user: '用户上传',
    generated_ai: 'AI 生成',
    stock_library: '素材库',
    downloaded: '外部导入',
  }
  return labels[source] || source || '图片素材'
}

function canDeleteMaterial(entry: MaterialLibraryEntry) {
  return Boolean(entry?.id)
}

function buildLinkedResultTitle(result: QueryResult) {
  return result.metadata?.title || result.metadata?.project_title || getResultLabel(result.source)
}

function canUseResultAsSceneImage(result: QueryResult) {
  return Boolean(linkedProjectId.value && linkedSceneNumber.value && result.metadata?.image_url)
}

function canUseResultAsConsistencyAssetImage(result: QueryResult) {
  return Boolean(linkedProjectId.value && linkedAssetType.value && linkedAssetId.value && result.metadata?.image_url)
}

async function copyContent(content: string) {
  try {
    await navigator.clipboard.writeText(content)
    ElMessage.success('已复制')
  } catch {
    ElMessage.error('复制失败，请检查浏览器剪贴板权限')
  }
}

function handleTabChange(tab: AssetTab) {
  activeTab.value = tab
}

async function handleQuery() {
  const normalizedQuery = queryForm.query.trim()
  if (!normalizedQuery) {
    ElMessage.warning('请输入要搜索的内容')
    return
  }

  querying.value = true
  queryResults.value = []

  try {
    const response = await request.post<{ results?: QueryResult[] }>('/knowledge/query', {
      query: normalizedQuery,
      top_k: queryForm.top_k,
      kb_type: queryForm.kb_type,
    })
    queryResults.value = response.results || []
    if (!queryResults.value.length) {
      ElMessage.info('没有找到相关参考资料')
    }
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '搜索失败')
  } finally {
    querying.value = false
  }
}

function handleClearProjectQuery() {
  queryForm.query = ''
  queryForm.top_k = 5
  queryForm.kb_type = isSceneImageMode.value ? 'scene' : 'all'
  queryResults.value = []
}

function quickQuery(query: string) {
  queryForm.query = query
  void handleQuery()
}

</script>

<style scoped>
.asset-shell {
  min-height: 100vh;
  padding: 28px;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 26%),
    radial-gradient(circle at bottom right, rgba(251, 191, 36, 0.14), transparent 24%),
    linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
}

.asset-panel {
  max-width: 1320px;
  margin: 0 auto;
  padding: 32px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.08);
}

.asset-hero,
.section-head,
.section-head--split,
.content-card__head,
.content-card__footer,
.result-card__head,
.asset-hero__actions,
.toolbar-inline,
.query-actions,
.result-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.asset-hero {
  align-items: flex-start;
}

.asset-hero h1 {
  margin: 6px 0 10px;
  color: #0f172a;
  font-size: clamp(30px, 4vw, 42px);
  font-weight: 800;
}

.asset-hero__eyebrow,
.section-label {
  margin: 0;
  color: #c2410c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.asset-hero__subtitle,
.section-head p,
.result-card__content,
.content-card__body {
  color: #475569;
  line-height: 1.8;
}

.asset-banner {
  margin-top: 24px;
}

.asset-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;
}

.asset-tab {
  padding: 18px 20px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(248, 250, 252, 0.86);
  text-align: left;
  cursor: pointer;
}

.asset-tab span {
  display: block;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
}

.asset-tab small {
  display: block;
  margin-top: 8px;
  color: #64748b;
  line-height: 1.6;
}

.asset-tab--active {
  border-color: rgba(59, 130, 246, 0.26);
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.94), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.12);
}

.asset-section {
  margin-top: 24px;
}

.query-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(320px, 0.7fr);
  gap: 18px;
}

.query-panel--single {
  grid-template-columns: minmax(0, 1fr);
}

.query-panel__main,
.content-card,
.result-card {
  padding: 22px;
  border-radius: 24px;
  background: rgba(248, 250, 252, 0.86);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.query-controls,
.result-list,
.content-grid {
  display: grid;
  gap: 16px;
}

.query-controls {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: end;
  margin-top: 18px;
}

.query-control {
  display: grid;
  gap: 8px;
}

.query-control span,
.stat-card span,
.content-card__time,
.content-card__footer span,
.result-card__time {
  color: #64748b;
  font-size: 13px;
}

.query-control--wide {
  min-width: 0;
}

.query-actions {
  align-items: center;
}

.quick-tags,
.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.quick-tags {
  margin-top: 18px;
}

.quick-tag {
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: #fff;
  color: #334155;
  cursor: pointer;
}

.result-list,
.content-grid {
  margin-top: 20px;
}

.content-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.content-card__head,
.content-card__footer,
.result-card__head {
  align-items: flex-start;
}

.content-card__identity {
  display: grid;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.content-card__body,
.result-card__content {
  margin: 14px 0 0;
  white-space: pre-wrap;
}

.content-card__body {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
}

.content-card__actions,
.result-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.content-card__actions--header {
  justify-content: flex-end;
  flex: 0 0 auto;
}

.result-card__preview {
  overflow: hidden;
  border-radius: 18px;
  background: #e2e8f0;
}

.result-card__preview {
  margin-top: 14px;
  min-height: 200px;
}

.result-card__preview img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.result-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.result-card__meta span {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #475569;
  font-size: 13px;
}

.toolbar-inline {
  align-items: center;
}

.toolbar-inline > * {
  min-width: 0;
}

@media (max-width: 1080px) {
  .asset-tabs,
  .query-panel,
  .query-controls,
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .asset-shell {
    padding: 16px;
  }

  .asset-panel {
    padding: 20px;
    border-radius: 20px;
  }

  .asset-hero,
  .asset-hero__actions,
  .section-head--split,
  .toolbar-inline,
  .content-card__head,
  .content-card__footer,
  .result-card__head,
  .query-actions,
.result-card__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .content-card__actions--header {
    justify-content: flex-start;
  }

  .asset-tab span {
    font-size: 16px;
  }
}
</style>